import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const VIDEO = "https://assets.mixkit.co/videos/43477/43477-720.mp4";
const POSTER = "https://assets.mixkit.co/videos/43477/43477-thumb-720-0.jpg";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

const BENEFITS = [
  {
    title: "Direct Input",
    body: "Help shape the platform, priorities and future features.",
  },
  {
    title: "Early Access",
    body: "Be among the first to use new tools and platform features.",
  },
  {
    title: "Permanent Recognition",
    body: "Receive a Founding Member badge on your player, club, community or academy profile.",
  },
  {
    title: "Priority Support",
    body: "Get dedicated help with onboarding, profiles, matches and tournaments.",
  },
];

function Tile({ title, body, tag, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex h-full flex-col border border-[#2A2A2A] bg-[#0B0B0B]/60 p-6 md:p-7"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display uppercase text-white text-2xl md:text-[1.7rem] leading-none tracking-[0.02em]">
          {title}
        </h3>
        {tag && (
          <span className="shrink-0 font-body text-[0.62rem] font-medium uppercase tracking-[0.2em] text-[#1F7A4D]">
            {tag}
          </span>
        )}
      </div>
      <p className="mt-3.5 font-body text-sm md:text-base font-light leading-relaxed text-[#B3B3B3]">
        {body}
      </p>
    </div>
  );
}

export default function FoundingMembers() {
  return (
    <section
      id="founding-members"
      data-testid="founding-members"
      className="relative z-20 overflow-hidden border-t border-[#2A2A2A] py-24 md:py-32"
    >
      {/* Background video */}
      <div className="absolute inset-0 z-0">
        <video
          data-testid="founding-video"
          className="h-full w-full object-cover object-center"
          src={VIDEO}
          poster={POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
      </div>
      {/* Strong dark overlay */}
      <div className="absolute inset-0 z-10 bg-[#0B0B0B]/82" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#0B0B0B] via-[#0B0B0B]/70 to-[#0B0B0B]/60" />

      {/* Content */}
      <div className="relative z-20 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left column */}
          <div>
            <motion.span
              {...fadeUp}
              transition={{ duration: 0.8, ease: EASE }}
              className="mb-7 flex items-center gap-3"
            >
              <span className="h-2 w-2 rounded-full bg-[#1F7A4D]" />
              <span className="font-body text-xs md:text-sm font-medium tracking-[0.3em] uppercase text-[#B3B3B3]">
                Founding Members
              </span>
            </motion.span>

            <motion.h2
              {...fadeUp}
              transition={{ duration: 0.9, ease: EASE, delay: 0.05 }}
              data-testid="founding-heading"
              className="font-display uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.88] tracking-[0.01em]"
            >
              Build the Future
              <br />
              Together.
            </motion.h2>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.9, ease: EASE, delay: 0.12 }}
              className="mt-8 max-w-[52ch] space-y-4 font-body text-lg font-light leading-relaxed text-[#B3B3B3]"
            >
              <p>
                We&apos;re inviting a small group of players, football
                communities, clubs and academies to join The Football Collective
                as founding members.
              </p>
              <p>
                Help shape the platform from the beginning and become part of the
                community building the digital home of grassroots football.
              </p>
            </motion.div>

            {/* 2x2 benefit grid */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.9, ease: EASE, delay: 0.16 }}
              className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2"
            >
              {BENEFITS.map((b) => (
                <Tile
                  key={b.title}
                  title={b.title}
                  body={b.body}
                  testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                />
              ))}
            </motion.div>

            {/* Optional benefit */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
              className="mt-5"
            >
              <Tile
                title="Featured Stories"
                tag="Optional"
                body="Selected founding members may be featured across The Football Collective's website and social channels."
                testid="benefit-featured-stories"
              />
            </motion.div>

            {/* CTA */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.9, ease: EASE, delay: 0.24 }}
              className="mt-10"
            >
              <a
                href="#kick-off"
                data-testid="founding-cta"
                className="inline-flex items-center gap-2.5 rounded-full bg-[#1F7A4D] px-8 py-4 font-body text-base font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-[#26945D] hover:scale-[1.03] active:scale-100"
              >
                Become a Founding Member
              </a>
            </motion.div>
          </div>

          {/* Right column — quote tile (secondary) */}
          <motion.figure
            {...fadeUp}
            transition={{ duration: 1, ease: EASE, delay: 0.14 }}
            data-testid="founding-quote"
            className="border border-[#2A2A2A] bg-[#0B0B0B]/50 p-8 md:p-10 lg:mt-[4.5rem]"
          >
            <span className="font-display text-5xl leading-none text-[#1F7A4D]">
              &ldquo;
            </span>
            <blockquote className="mt-4 font-body text-2xl md:text-[1.75rem] font-light leading-relaxed text-white/90">
              The first people to join will help shape what grassroots football
              looks like online for everyone who follows.
            </blockquote>
            <figcaption className="mt-7 font-body text-xs uppercase tracking-[0.24em] text-[#B3B3B3]">
              &mdash; The Football Collective
            </figcaption>
          </motion.figure>
        </div>
      </div>
    </section>
  );
}
